import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, clearSession, loadSession, saveSession, type StoredSession } from './lib/api';
import { useRoute } from './lib/route';
import { Shell } from './components/Shell';
import { Account } from './pages/Account';
import { Accounts } from './pages/Accounts';
import { Albums } from './pages/Albums';
import { Audit } from './pages/Audit';
import { Frame } from './pages/Frame';
import { Frames } from './pages/Frames';
import { NotYet } from './pages/NotYet';
import { Overview } from './pages/Overview';
import { Photos } from './pages/Photos';
import { Screens } from './pages/Screens';
import { SignIn } from './pages/SignIn';

export const App = () => {
  const [session, setSession] = useState<StoredSession | null>(loadSession);
  const [checked, setChecked] = useState(false);
  const { route, query } = useRoute();

  // A stored token may have been revoked, or the address taken off the list
  // since it was minted. Ask before showing a console that would 403 on first
  // use — and on 403 drop the session rather than keep a credential this
  // origin has no use for.
  useEffect(() => {
    const stored = loadSession();
    if (!stored) {
      setChecked(true);
      return;
    }
    let live = true;
    api
      .whoAmI(stored.token)
      .then((who) => {
        if (!live) return;
        const next = { token: stored.token, account: { ...stored.account, ...who.account } };
        saveSession(next);
        setSession(next);
      })
      .catch((caught: unknown) => {
        if (!live) return;
        if (caught instanceof ApiError && caught.status === 403) {
          void api.signOut(stored.token).catch(() => undefined);
        }
        clearSession();
        setSession(null);
      })
      .finally(() => live && setChecked(true));
    return () => {
      live = false;
    };
  }, []);

  const onSignedIn = useCallback((next: StoredSession) => {
    saveSession(next);
    setSession(next);
  }, []);

  const signOut = useCallback(() => {
    const current = loadSession();
    if (current) void api.signOut(current.token).catch(() => undefined);
    clearSession();
    setSession(null);
    window.location.hash = '';
  }, []);

  if (!checked) {
    return (
      <main className="signin">
        <p className="eyebrow">Loading</p>
      </main>
    );
  }

  if (!session) return <SignIn onSignedIn={onSignedIn} />;

  const token = session.token;
  const page =
    route.kind === 'accounts' ? (
      <Accounts token={token} query={query} />
    ) : route.kind === 'account' ? (
      <Account token={token} id={route.id} selfId={session.account.id} />
    ) : route.kind === 'frames' ? (
      <Frames token={token} query={query} />
    ) : route.kind === 'frame' ? (
      <Frame token={token} id={route.id} />
    ) : route.kind === 'photos' ? (
      <Photos token={token} query={query} />
    ) : route.kind === 'screens' ? (
      <Screens token={token} query={query} />
    ) : route.kind === 'albums' ? (
      <Albums token={token} query={query} />
    ) : route.kind === 'audit' ? (
      <Audit token={token} query={query} />
    ) : route.kind === 'subscriptions' ? (
      <NotYet kind="subscriptions" />
    ) : route.kind === 'email' ? (
      <NotYet kind="email" />
    ) : (
      <Overview token={token} />
    );

  return (
    <Shell route={route} who={session.account.email} onSignOut={signOut}>
      {page}
    </Shell>
  );
};
